// @ts-check
/**
 * Channels — a cross-thread Pub/Sub over `MessagePort`, with a hub.
 *
 * Design: `testing/iso/ptf/2026/documentation/2026-08-20-Pub-Sub-Using-Async-Atomics.md`
 * (§9 is the shape below; §1–§8 are why it is a `MessagePort` hub and not
 * `Atomics.waitAsync` or `BroadcastChannel`).
 *
 * Since 2026-09-21 the two classes and the grammar they share are the
 * `channels` aspect of `@smotaal/platform-orchestration`, the incubator's
 * package that this tree's `helpers/channels.js` and `classes/channels/` were
 * carried into on 2026-09-17 (`platform/packages/platform-orchestration`),
 * reached by the relative path under `packages/@smotaal/` so that a worker
 * loads it too. This helper is the folder contract the tree keeps
 * (`CONVENTIONS.md` §5): the package's functions of this concern, and the
 * one thing that is the tree's own, which hub a page opens.
 *
 * | Class | Runs where | Does |
 * | --- | --- | --- |
 * | `ChannelEndpoint` | a page, a worker, a tab | `subscribe(topic, listener) → unsubscribe`, `publish(topic, data, transfer?)` |
 * | `ChannelHub` | a `SharedWorker` where available, else a `Worker` | routes publications to the endpoints whose declared topics match, coalescing bursts |
 *
 * A class has one import path, the package's index:
 * `packages/@smotaal/platform-orchestration/platform-orchestration.js`.
 *
 * **Topics are strings the application chooses.** This module knows nothing
 * about what travels on them: it matches, coalesces and delivers. A topic is
 * `/`-separated (`usage`, `segments/<key>`); a pattern is a topic, or a topic
 * whose LAST segment is `*`, which stands for one or more further segments.
 * `*` alone matches everything.
 *
 * **Addressed, not broadcast.** Every endpoint tells the hub which topics it
 * listens to, so a publication for one endpoint reaches one — measured at 30×
 * over fan-out-to-all (§4.5). An endpoint never hears its own publication: the
 * hub excludes the publisher, so a worker that publishes a change is not asked
 * to adopt it.
 *
 * **Coalescing lives in the hub.** Publications on one topic within the
 * window collapse to one. By default array-valued fields of the data are
 * UNIONED across the burst, in first-seen order, and every other field takes
 * the latest value — so a burst that names paths does not lose any of them.
 * A publication merged from different origins keeps no origin, and reaches
 * both.
 *
 * **Not a global.** Nothing here is shared state at module level: every hub
 * and every endpoint is an instance handed the port it works on.
 *
 * @module helpers/channels
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1 (code generation)
 */

import { DEFAULT_HUB_NAME, openHub as openPackageHub } from '../packages/@smotaal/platform-orchestration/channels.js';

export {
    DEFAULT_COALESCE_MILLISECONDS, DEFAULT_HUB_NAME, assertTopic, matchesTopic, mergePublications,
} from '../packages/@smotaal/platform-orchestration/channels.js';

/**
 * A topic pattern: exact, or a trailing `*` segment wildcard.
 *
 * @typedef {string} Topic
 */

/**
 * @template [T=unknown]
 * @typedef {{
 *   topic: string,
 *   data: T,
 *   origin: string | null,
 *   at: number,
 * }} Publication `origin` is the publishing endpoint's name; `null` when a
 *   coalesced publication came from more than one. `at` is the publisher's
 *   clock, for ordering only.
 */

/** @typedef {{ postMessage: Function, start?: Function, close?: Function, onmessage: any, onmessageerror?: any }} PortLike */

// ========================================
// Opening the hub from a page
// ========================================

/**
 * Opens the origin's hub: the `SharedWorker` at
 * `classes/channels/channel-hub-entrypoint.js` where the browser has one, else
 * a dedicated `Worker` running the same module for this tab alone.
 *
 * The URL is derived HERE, from `import.meta.url`, so no application ever
 * constructs another's — a `SharedWorker`'s identity is its URL and name, and
 * two pages that open it this way share one hub. The package's `openHub`
 * takes no default, since the entrypoint is the application's; this is the
 * tree's.
 *
 * @param {{ url?: URL | string, name?: string, dedicated?: boolean }} [options]
 *   `dedicated` forces the per-tab `Worker` — the fallback a page takes when
 *   the shared one failed to start.
 * @returns {{ port: PortLike, kind: 'SharedWorker' | 'Worker', source: SharedWorker | Worker }}
 */
export function openHub({
    url = new URL('../classes/channels/channel-hub-entrypoint.js', import.meta.url),
    name = DEFAULT_HUB_NAME,
    dedicated = false,
} = {}) {
    return /** @type {{ port: PortLike, kind: 'SharedWorker' | 'Worker', source: SharedWorker | Worker }} */ (openPackageHub({ url, name, dedicated }));
}
